import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, usePage } from '@inertiajs/react';
import { cn } from '../../lib/cn';

const STORAGE_KEY = 'studyfit-sidebar-collapsed';

// ── SVG Icons ──────────────────────────────────────────────
function DashboardIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function CoursesIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>
  );
}

function LibraryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="18" x="3" y="3" rx="1" />
      <path d="M7 3v18" />
      <path d="M20.4 18.9c.2.5-.1 1.1-.6 1.3l-1.9.7c-.5.2-1.1-.1-1.3-.6L11.1 5.1c-.2-.5.1-1.1.6-1.3l1.9-.7c.5-.2 1.1.1 1.3.6Z" />
    </svg>
  );
}

function EssayComposerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M12 12H9.5a2.5 2.5 0 0 1 0-5H17" />
      <path d="M12 7v10" />
      <path d="M16 7v10" />
    </svg>
  );
}

function GraphingCalculatorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M7 16c.5-2 1.5-7 4-7 2 0 2 3 4 3 2.5 0 4.5-5 5-7" />
    </svg>
  );
}

function ActivityGeneratorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
      <path d="m10 8-3 3 3 3" />
      <path d="m14 14 3-3-3-3" />
    </svg>
  );
}

function DictionaryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
      <path d="m8 13 4-7 4 7" />
      <path d="M9.1 11h5.7" />
    </svg>
  );
}

function AtlasIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function SentenceStudioIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4h1a2 2 0 0 1 2 2 2 2 0 0 1 2-2h1" />
      <path d="M12 20h-1a2 2 0 0 1-2-2 2 2 0 0 1-2 2H6" />
      <path d="M5 16H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h1" />
      <path d="M13 8h7a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-7" />
      <path d="M9 6v12" />
    </svg>
  );
}

function LiveSessionIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <path d="M15.033 9.44a.647.647 0 0 1 0 1.12l-4.065 2.352a.645.645 0 0 1-.968-.56V7.648a.645.645 0 0 1 .967-.56z" />
      <path d="M7 21h10" />
    </svg>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function TasksIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

function ConceptsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function AssessmentsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function FlagIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
  );
}

function CollapseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <polyline points="14 9 17 12 14 15" />
    </svg>
  );
}

function ExpandIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <polyline points="17 9 14 12 17 15" />
    </svg>
  );
}

function MenuIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ── Icon map ───────────────────────────────────────────────
const ICONS = {
  dashboard: DashboardIcon,
  courses: CoursesIcon,
  library: LibraryIcon,
  essayComposer: EssayComposerIcon,
  graphingCalculator: GraphingCalculatorIcon,
  activityGenerator: ActivityGeneratorIcon,
  dictionary: DictionaryIcon,
  atlas: AtlasIcon,
  calendar: CalendarIcon,
  tasks: TasksIcon,
  concepts: ConceptsIcon,
  sentenceStudio: SentenceStudioIcon,
  liveSession: LiveSessionIcon,
  assessments: AssessmentsIcon,
  users: UsersIcon,
  flags: FlagIcon,
};

// Looks up the SVG icon component for a given key (matches Sidebar's `icon`
// values). Used by TopBar / breadcrumb to prefix titles with the relevant
// section icon — exported so any component can resolve a sidebar-style icon.
export function NavIcon({ iconKey, className }) {
  const Cmp = iconKey && ICONS[iconKey];
  if (!Cmp) return null;
  return <Cmp className={className} />;
}

// Resolve which sidebar icon key should represent the current URL. Walks
// known nav items (teacher + student) plus a small list of overrides for
// pages that aren't directly in the sidebar (live session join URL,
// activity show pages, etc.) and returns the longest-prefix match.
const URL_ICON_OVERRIDES = [
  { match: '/teacher/live_sessions', icon: 'liveSession' },
  { match: '/student/live_sessions', icon: 'liveSession' },
  { match: '/student/vocabulary', icon: 'dictionary' },
  { match: '/teacher/sentence_studio', icon: 'sentenceStudio' },
  { match: '/student/sentence_studio', icon: 'sentenceStudio' },
];

export function resolveNavIconKey(url, role) {
  if (!url) return null;
  const navItems =
    role === 'teacher' || role === 'admin'
      ? getTeacherNavItems(role)
      : getStudentNavItems();
  const candidates = [...navItems, ...URL_ICON_OVERRIDES];
  let best = null;
  let bestLen = 0;
  for (const item of candidates) {
    const path = item.match || item.href;
    if (!path || !item.icon) continue;
    if (url === path || url.startsWith(path + '/') || url === path) {
      if (path.length > bestLen) {
        best = item.icon;
        bestLen = path.length;
      }
    } else if (url.startsWith(path)) {
      if (path.length > bestLen) {
        best = item.icon;
        bestLen = path.length;
      }
    }
  }
  return best;
}

// ── Nav item configs by role ───────────────────────────────
export function getTeacherNavItems(role, t) {
  // If t() isn't passed (caller forgot, or i18n hasn't booted yet) we fall
  // back to the inline string. We also fall back when t() returns the *key*
  // unchanged — that's i18next's behavior when the bundle is missing the
  // entry, and rendering "nav.atlas" in the sidebar is worse than just
  // showing "Atlas".
  const label = (key, fallback) => {
    if (!t) return fallback;
    const full = `nav.${key}`;
    const v = t(full);
    return v === full ? fallback : v;
  };
  const items = [
    // ── Active ──
    { key: 'dashboard', label: label('dashboard', 'Dashboard'), href: '/teacher/dashboard', icon: 'dashboard' },
    { key: 'courses', label: label('courses', 'Courses'), href: '/teacher/courses', icon: 'courses' },
    { key: 'library', label: label('library', 'Library'), href: '/teacher/library', icon: 'library' },
    { key: 'dictionary', label: label('dictionary', 'Dictionary'), href: '/teacher/dictionary', icon: 'dictionary' },
    { key: 'atlas', label: label('atlas', 'Atlas'), href: '/atlas', icon: 'atlas' },
    { key: 'calendar', label: label('calendar', 'Calendar'), href: '/teacher/calendar', icon: 'calendar' },
    { key: 'sentenceStudio', label: label('sentenceStudio', 'Sentence Studio'), href: '/teacher/sentence_studio', icon: 'sentenceStudio' },
    { key: 'assessments', label: label('assessments', 'Assessments'), href: '/teacher/assessments', icon: 'assessments' },
  ];

  if (role === 'admin') {
    items.push({ key: 'users', label: label('users', 'Users'), href: '/admin/users', icon: 'users' });
    items.push({ key: 'vocabularyFlags', label: 'Vocab Flags', href: '/admin/vocabulary_flags', icon: 'flags' });
  }

  // ── Active (new) ──
  items.push(
    { key: 'concepts', label: label('concepts', 'Concepts'), href: '/teacher/concepts/grammar', match: '/teacher/concepts', icon: 'concepts' },
    { key: 'liveSession', label: label('liveSession', 'Live Session'), href: '/teacher/live_sessions', icon: 'liveSession' },
  );

  // ── Inactive (coming soon) ──
  items.push(
    { key: 'essayComposer', label: label('essayComposer', 'Essay Composer'), icon: 'essayComposer', disabled: true },
    { key: 'tasks', label: label('tasks', 'Tasks'), icon: 'tasks', disabled: true },
    { key: 'graphingCalculator', label: label('graphingCalculator', 'Graphing Calculator'), icon: 'graphingCalculator', disabled: true },
    { key: 'activityGenerator', label: label('activityGenerator', 'Activity Generator'), icon: 'activityGenerator', disabled: true },
  );

  return items;
}

export function getStudentNavItems(t) {
  // If t() isn't passed (caller forgot, or i18n hasn't booted yet) we fall
  // back to the inline string. We also fall back when t() returns the *key*
  // unchanged — that's i18next's behavior when the bundle is missing the
  // entry, and rendering "nav.atlas" in the sidebar is worse than just
  // showing "Atlas".
  const label = (key, fallback) => {
    if (!t) return fallback;
    const full = `nav.${key}`;
    const v = t(full);
    return v === full ? fallback : v;
  };
  return [
    { key: 'dashboard', label: label('dashboard', 'Dashboard'), href: '/student/dashboard', icon: 'dashboard' },
    { key: 'dictionary', label: label('vocabulary', 'Vocabulary'), href: '/student/dictionary', icon: 'dictionary' },
    { key: 'sentenceStudio', label: label('sentenceStudio', 'Sentence Studio'), href: '/student/sentence_studio', icon: 'sentenceStudio' },
    { key: 'atlas', label: label('atlas', 'Atlas'), href: '/atlas', icon: 'atlas' },
    { key: 'liveSession', label: label('liveSession', 'Live Session'), href: '/student/live_sessions', icon: 'liveSession' },
  ];
}

// ── Sidebar component ──────────────────────────────────────
export default function Sidebar({ items }) {
  const { t } = useTranslation();
  const { url } = usePage();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed);
  }, [collapsed]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [url]);

  return (
    <>
      {/* Mobile hamburger button — rendered in top bar via portal-like approach */}
      <MobileMenuButton onClick={() => setMobileOpen(true)} />

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-black/30"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-44 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-50 flex flex-col">
            <div className="flex items-center justify-between h-14 px-4 border-b border-gray-200 dark:border-gray-700">
              <span className="text-lg font-bold text-gray-900 dark:text-white">{t('common.studyfit')}</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 py-3 px-2 overflow-y-auto">
              {items.map((item) => (
                <SidebarItem key={item.key} item={item} collapsed={false} url={url} />
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        data-sidebar
        className={cn(
          'hidden md:flex flex-col flex-shrink-0',
          'bg-white dark:bg-gray-900',
          'border-r border-gray-200 dark:border-gray-700',
          'transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-44'
        )}
      >
        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {items.map((item) => (
            <SidebarItem key={item.key} item={item} collapsed={collapsed} url={url} />
          ))}
        </nav>

        {/* Collapse toggle — matches StickyFooter h-16 so the bottom edges align */}
        <div className="border-t border-gray-200 dark:border-gray-700 h-16 flex items-center px-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'flex items-center w-full rounded-lg px-3 py-2',
              'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
              'dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800',
              'transition-colors',
              collapsed && 'justify-center px-0'
            )}
            title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          >
            {collapsed ? (
              <ExpandIcon className="w-5 h-5 flex-shrink-0" />
            ) : (
              <>
                <CollapseIcon className="w-5 h-5 flex-shrink-0" />
                <span className="ml-3 text-sm font-medium">{t('nav.collapse')}</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Soft blue glow used by the Live Session sidebar item when an
          active session is in flight. SVG drop-shadow animates a halo
          without affecting layout. */}
      <style>{`
        @keyframes sidebar-live-glow-pulse {
          0%, 100% {
            filter: drop-shadow(0 0 3px rgba(59, 130, 246, 0.45));
          }
          50% {
            filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.75));
          }
        }
        .sidebar-live-glow {
          animation: sidebar-live-glow-pulse 2.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .sidebar-live-glow {
            animation: none;
            filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.55));
          }
        }
      `}</style>
    </>
  );
}

// ── Sidebar item ───────────────────────────────────────────
function SidebarItem({ item, collapsed, url }) {
  const { active_live_session, open_vocabulary_flags_count } = usePage().props;
  const Icon = ICONS[item.icon];

  // The Live Session item gets a soft blue glow when there's an active
  // session in flight. The href stays pointed at the lobby (index) so the
  // user always lands somewhere they can act on, rather than getting
  // trapped in a single Show page.
  const liveActive = item.key === 'liveSession' && !!active_live_session;
  const flagBadge = item.key === 'vocabularyFlags' && open_vocabulary_flags_count > 0
    ? open_vocabulary_flags_count
    : null;
  const href = item.href;

  const matchPath = item.match || href;
  const isActive = matchPath && (
    matchPath === '/'
      ? url === '/'
      : url.startsWith(matchPath)
  );

  const content = (
    <div
      className={cn(
        'group relative flex items-center rounded-lg px-3 py-2 my-0.5',
        'transition-colors',
        collapsed && 'justify-center px-0',
        item.disabled
          ? 'text-gray-300 dark:text-gray-600 cursor-default'
          : isActive
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-medium'
            : liveActive
              ? 'text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800/50'
      )}
      title={collapsed ? (liveActive ? `${item.label} · live` : item.label) : undefined}
    >
      {Icon && (
        <Icon
          className={cn(
            'w-5 h-5 flex-shrink-0',
            item.disabled && 'opacity-40',
            liveActive && 'sidebar-live-glow'
          )}
        />
      )}
      {!collapsed && (
        <span className={cn('ml-3 text-sm', item.disabled && 'opacity-40')}>
          {item.label}
        </span>
      )}
      {!collapsed && liveActive && (
        <span
          className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400 sidebar-live-glow"
          title="Live session in progress"
        />
      )}
      {!collapsed && flagBadge && (
        <span className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-500 text-white text-xs font-medium flex items-center justify-center leading-none">
          {flagBadge > 99 ? '99+' : flagBadge}
        </span>
      )}
      {collapsed && flagBadge && (
        <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-500" />
      )}
    </div>
  );

  if (item.disabled || !href) {
    return content;
  }

  return (
    <Link href={href} className="block">
      {content}
    </Link>
  );
}

// ── Mobile menu button (exposed for top bar) ───────────────
export function MobileMenuButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="md:hidden p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
    >
      <MenuIcon className="w-5 h-5" />
    </button>
  );
}
